import GrooveViewObject from '@groove/api/visualforce/types/GrooveContext/GrooveViewObject';

import {
  OrganizationConfiguration,
  ContextDetails,
  LightningOutRenderer,
  CreateComponentStatus,
} from './types';

declare global {
  interface Window {
    $Lightning: {
      use: (
        applicationName: string,
        onInitialized: () => void,
        lightningUrl: string,
        accessToken: string,
      ) => void;
      createComponent: (
        componentName: string,
        properties: Record<string, unknown>,
        element: HTMLElement | string,
        onCreated: (
          component: any, // eslint-disable-line @typescript-eslint/no-explicit-any
          status: CreateComponentStatus,
          errorMessage: string,
        ) => void,
      ) => void;
    };
  }
}

export default class LightningOut {
  private accessToken: string;

  private lightningUrl: string;

  private configuration: OrganizationConfiguration;

  private subscriptions: Array<() => void>;

  private renderer: LightningOutRenderer;

  constructor(
    renderer: LightningOutRenderer,
    accessToken = '',
    sfdcInstanceUrl = '',
    configuration: OrganizationConfiguration = {},
  ) {
    this.renderer = renderer;
    this.subscriptions = [];
    this.accessToken = '';
    this.lightningUrl = '';
    this.configuration = {};
    this.subscriptions = [];

    this.configure(accessToken, sfdcInstanceUrl, configuration);
  }

  subscribe(onChange: () => void): () => void {
    if (this.subscriptions.indexOf(onChange) < 0) {
      this.subscriptions.push(onChange);
    }

    return onChange;
  }

  unSubscribe(onChange: () => void): void {
    this.subscriptions = this.subscriptions.filter(s => s !== onChange);
  }

  trigger(): void {
    this.subscriptions.forEach(onChange => onChange());
  }

  async waitForAccessToken(): Promise<void> {
    return new Promise<void>(resolve => {
      if (this.accessToken) {
        resolve();
        return;
      }

      let unsubscribe: () => void;
      const handler = (): void => {
        if (this.accessToken) {
          resolve();
          this.unSubscribe(unsubscribe);
        }
      };
      unsubscribe = this.subscribe(handler);
    });
  }

  /**
   * Set all properties and trigger all subscriptions
   */
  configure(
    accessToken: string,
    sfdcInstanceUrl: string,
    configuration: OrganizationConfiguration,
    onInitializationError?: (e: Error) => void,
    onComponentError?: (msg: string) => void,
  ): void {
    this.accessToken = accessToken;
    this.configuration = configuration;
    this.lightningUrl = sfdcInstanceUrl.replace(
      'my.salesforce',
      'lightning.force',
    );

    if (onInitializationError) {
      this.renderer.onInitializationError = onInitializationError;
    }

    if (onComponentError) {
      this.renderer.onComponentError = onComponentError;
    }

    this.trigger();
  }

  buildContextDetails(primaryObject: GrooveViewObject): ContextDetails {
    const id = primaryObject?.attributes?.id || '';
    const label = primaryObject?.attributes?.label || '';
    return {
      id,
      label,
      banner:
        (this.configuration[label] && this.configuration[label].banner) || [],
      tabs: (this.configuration[label] && this.configuration[label].tabs) || [],
    };
  }

  async render(
    container: HTMLElement,
    lightningComponents: string[],
    recordId: string,
  ): Promise<void> {
    await this.renderer.render(
      container,
      lightningComponents,
      recordId,
      this.lightningUrl,
      this.accessToken,
    );
  }
}
